import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../services/api';
import { Loader2, Sparkles, ArrowRight, ArrowLeft, BookOpen } from 'lucide-react';

type Step = 'outline' | 'generating' | 'preview';

export default function CreateCourse() {
  const navigate = useNavigate();
  const [step, setStep] = useState<Step>('outline');
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [outline, setOutline] = useState('');
  const [generatedContent, setGeneratedContent] = useState('');
  const [isGenerating, setIsGenerating] = useState(false);
  const [error, setError] = useState('');

  const sampleOutline = `Section 1: Introduction to Data Science
1.1 What is Data Science?
1.2 The Data Science Lifecycle
1.3 Tools and Technologies

Section 2: Data Analysis Fundamentals  
2.1 Descriptive Statistics
2.2 Data Visualization Basics
2.3 Exploratory Data Analysis

Section 3: Python for Data Science
3.1 NumPy Fundamentals
3.2 Pandas for Data Manipulation
3.3 Matplotlib and Seaborn`;

  const handleGenerate = async () => {
    if (!title.trim() || !outline.trim()) {
      setError('Please provide both title and outline');
      return;
    }

    setError('');
    setIsGenerating(true);
    setStep('generating');

    try {
      const course = await api.createCourse(title, description, outline);
      setGeneratedContent(course.content || '');
      setStep('preview');
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to generate course');
      setStep('outline');
    } finally {
      setIsGenerating(false);
    }
  };

  const handlePublish = async () => {
    // Navigate to edit page for further refinement
    navigate('/teacher');
  };

  return (
    <div className="max-w-4xl mx-auto px-6 py-8">
      {/* Header */}
      <div className="mb-8">
        <button
          onClick={() => navigate('/teacher')}
          className="flex items-center gap-2 text-apple-text-secondary hover:text-apple-text mb-4"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to Dashboard
        </button>
        <h1 className="text-3xl font-bold text-apple-text mb-2">Create New Course</h1>
        <p className="text-apple-text-secondary">
          Use AI to generate comprehensive course content from your outline
        </p>
      </div>

      {/* Progress */}
      <div className="flex items-center gap-4 mb-8">
        {['Outline', 'Generating', 'Preview'].map((s, i) => {
          const stepNames: Step[] = ['outline', 'generating', 'preview'];
          const currentIndex = stepNames.indexOf(step);
          const isActive = i <= currentIndex;
          const isCurrent = i === currentIndex;
          
          return (
            <React.Fragment key={s}>
              <div className={`flex items-center gap-2 ${isActive ? 'text-apple-blue' : 'text-apple-text-secondary'}`}>
                <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${
                  isCurrent 
                    ? 'bg-apple-blue text-white' 
                    : isActive 
                    ? 'bg-blue-100 text-apple-blue' 
                    : 'bg-gray-100 text-apple-text-secondary'
                }`}>
                  {i + 1}
                </div>
                <span className="font-medium">{s}</span>
              </div>
              {i < 2 && (
                <div className={`w-12 h-0.5 ${i < currentIndex ? 'bg-apple-blue' : 'bg-gray-200'}`} />
              )}
            </React.Fragment>
          );
        })}
      </div>

      {/* Error */}
      {error && (
        <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-apple-sm text-red-600">
          {error}
        </div>
      )}

      {/* Step Content */}
      {step === 'outline' && (
        <div className="card space-y-6">
          <div>
            <label className="block text-sm font-medium text-apple-text mb-2">
              Course Title *
            </label>
            <input
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              className="input"
              placeholder="e.g., Data Science 101"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-apple-text mb-2">
              Description
            </label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              className="input min-h-[100px] resize-none"
              placeholder="Brief description of the course"
              rows={3}
            />
          </div>

          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="block text-sm font-medium text-apple-text">
                Course Outline *
              </label>
              <button
                onClick={() => setOutline(sampleOutline)}
                className="text-xs text-apple-blue hover:underline"
              >
                Load Sample
              </button>
            </div>
            <textarea
              value={outline}
              onChange={(e) => setOutline(e.target.value)}
              className="input min-h-[300px] resize-none font-mono text-sm"
              placeholder={`Enter your course outline...

Example:
Section 1: Introduction
1.1 Topic A
1.2 Topic B

Section 2: Advanced Topics
2.1 Topic C
2.2 Topic D`}
              rows={12}
            />
            <p className="mt-2 text-xs text-apple-text-secondary">
              Use a hierarchical format with sections and subsections. The AI will expand each section into detailed content.
            </p>
          </div>

          <div className="flex justify-end">
            <button
              onClick={handleGenerate}
              disabled={!title.trim() || !outline.trim()}
              className="btn-primary flex items-center gap-2 disabled:opacity-50"
            >
              <Sparkles className="w-5 h-5" />
              Generate Course
            </button>
          </div>
        </div>
      )}

      {step === 'generating' && (
        <div className="card text-center py-16">
          <div className="relative w-20 h-20 mx-auto mb-6">
            <div className="absolute inset-0 border-4 border-blue-100 rounded-full" />
            <div className="absolute inset-0 border-4 border-apple-blue rounded-full border-t-transparent animate-spin" />
            <Sparkles className="absolute inset-0 m-auto w-8 h-8 text-apple-blue" />
          </div>
          <h3 className="text-xl font-semibold text-apple-text mb-2">
            Generating your course
          </h3>
          <p className="text-apple-text-secondary max-w-md mx-auto">
            Our AI is creating comprehensive HTML content based on your outline. 
            This may take a minute or two...
          </p>
        </div>
      )}

      {step === 'preview' && (
        <div className="space-y-6">
          <div className="card">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-apple-text">Generated Content Preview</h3>
              <div className="flex items-center gap-2">
                <span className="inline-flex items-center gap-1 px-3 py-1 bg-green-100 text-green-800 rounded-full text-sm">
                  <Sparkles className="w-4 h-4" />
                  AI Generated
                </span>
              </div>
            </div>
            
            <div className="border border-apple-border rounded-apple-sm overflow-hidden">
              <div className="bg-slate-50 p-3 border-b border-apple-border flex items-center gap-2">
                <BookOpen className="w-4 h-4 text-apple-text-secondary" />
                <span className="text-sm text-apple-text-secondary">HTML Preview</span>
              </div>
              <div 
                className="p-6 max-h-[500px] overflow-y-auto ds-content"
                dangerouslySetInnerHTML={{ __html: generatedContent }}
              />
            </div>
          </div>

          <div className="flex items-center justify-end gap-4">
            <button
              onClick={() => setStep('outline')}
              className="btn-outline"
            >
              Start Over
            </button>
            <button
              onClick={handlePublish}
              className="btn-primary flex items-center gap-2"
            >
              Go to Dashboard
              <ArrowRight className="w-5 h-5" />
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
